import * as React from 'react';
import { ReactElement } from 'react';
import PropTypes from 'prop-types';
import { TreeProps as RcTreeProps } from 'rc-tree';
import { SxProps } from '@mui/material';
import { DataNode } from 'rc-tree/lib/interface';

import renderSwitcherIcon from './renderSwitcherIcon';
import { TreeRoot, TreeClasses } from './TreeRoot';
import rcTreeMotion from './rcTreeMotion';
import NodeTitle from './NodeTitle';
import { NodeActionsProps } from './NodeActions';
import { DropIndicator } from './DropIndicator';

/**
 * Wrapper for rc-tree <Tree>, with Material Design style.
 *
 * @see https://github.com/react-component/tree#tree-props
 *
 * rc-tree expects a treeData prop containing a tree of nodes with key, title,
 * and children fields.
 *
 * @example // treeData format
 *
 * [
 *   { key: '1', title: 'foo1', children: [
 *     { key: '3', title: 'foo3', children: [
 *       { key: '5', title: 'foo5', children: [] },
 *     ] },
 *     { key: '4', title: 'foo4', children: [] },
 *   ] },
 *   { key: '2', title: 'foo2', children: [] },
 * ]
 *
 * @example // usage
 *
 * import { Tree } from '@react-admin/ra-tree';
 * import treeData from './treeData';
 *
 * export const SimpleTree: FC = () => <Tree treeData={treeData} />;
 */
export const Tree = ({
    className,
    showIcon = false,
    showLine = false,
    nodeActions,
    switcherIcon,
    sx,
    ...props
}: TreeProps): ReactElement => {
    return (
        <TreeRoot
            className={className}
            sx={sx}
            switcherIcon={(nodeProps): any =>
                renderSwitcherIcon(
                    TreeClasses.smallIcon,
                    switcherIcon,
                    showLine,
                    nodeProps
                )
            }
            titleRender={(data): any => (
                <NodeTitle data={data as DataNode} nodeActions={nodeActions} />
            )}
            expandAction="click"
            motion={rcTreeMotion}
            showLine={showLine}
            showIcon={showIcon}
            dropIndicatorRender={DropIndicator}
            {...props}
        />
    );
};

Tree.propTypes = {
    showLine: PropTypes.bool,
    switcherIcon: PropTypes.any,
};

export interface TreeProps
    extends Omit<RcTreeProps, 'prefixCls' | 'children' | 'expandAction'> {
    className?: string;
    expandAction?: RcTreeProps['expandAction'];
    nodeActions?: ReactElement<NodeActionsProps>;
    switcherIcon?: any;
    sx?: SxProps;
}
